import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Link,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axiosInstance from '../../../services/axiosInstance';
import { toast } from 'react-toastify';
import { UserContext } from '../../../hooks';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const TotalPaymentDialog = ({
  open,
  onClose,
  bookingId,
  totalAmount,
  player,
}) => {
  const stripe = useStripe();
  const navigate = useNavigate();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [savedCards, setSavedCards] = useState([]); // Tarjetas guardadas
  const [paymentMethod, setPaymentMethod] = useState('wallet'); // "wallet" | "card"
  const [selectedCard, setSelectedCard] = useState('new'); // "new" | "saved-card-id"
  const [saveCard, setSaveCard] = useState(false); // Nuevo checkbox para guardar tarjeta
  const { user } = useContext(UserContext);

  // Obtener el saldo del monedero cuando se abre el modal
  useEffect(() => {
    if (open) {
      axiosInstance
        .get('/users/wallet')
        .then(response => setWalletBalance(response.balance))
        .catch(error => console.error('Error obteniendo el saldo:', error));

      axiosInstance
        .get('/users/saved-cards') // Obtener tarjetas guardadas
        .then(response => setSavedCards(response.cards))
        .catch(error =>
          console.error('Error obteniendo tarjetas guardadas:', error)
        );
    }
  }, [open]);

  const handlePayment = async () => {
    setLoading(true);

    try {
      if (paymentMethod === 'wallet' && walletBalance >= totalAmount) {
        // PAGAR CON SALDO DEL MONEDERO
        const response = await axiosInstance.post(
          '/users/booking/payWithWallet',
          {
            bookingId,
            playerIndex: player ? player.index : null,
          }
        );

        if (response.success) {
          toast.success(
            player
              ? player.id === user.id
                ? `Has pagado tu parte de la reserva con saldo del monedero: ${response.walletUsed}€`
                : `Has pagado la parte de ${player.memberId.name} con saldo del monedero: ${response.walletUsed}€`
              : `Has pagado la reserva completa con saldo del monedero: ${response.walletUsed}€`
          );
          onClose();
        } else {
          toast.error(
            response.message || 'Error al procesar el pago con monedero.'
          );
        }
      } else {
        // PAGAR CON TARJETA (Stripe)
        if (!stripe || !elements) {
          alert();
          toast.error('Stripe no está listo. Por favor, intenta nuevamente.');
          return;
        }

        let paymentMethodId = selectedCard;

        if (selectedCard === 'new') {
          // Crear un nuevo PaymentMethod si el usuario ingresó una nueva tarjeta
          const cardElement = elements.getElement(CardElement);
          const result = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });

          if (result.error) {
            toast.error(
              `Error al procesar la tarjeta: ${result.error.message}`
            );
            setLoading(false);
            return;
          }

          paymentMethodId = result.paymentMethod.id; // Usar el ID de la tarjeta creada
        }

        if (!paymentMethodId) {
          toast.error('Error: No se ha seleccionado un método de pago válido.');
          setLoading(false);
          return;
        }

        // Enviar `paymentMethodId` al backend para confirmar el pago
        const response = await axiosInstance.post(
          '/users/booking/createPaymentIntent',
          {
            bookingId,
            playerIndex: player ? player.index : null,
            saveCard,
            paymentMethodId, // Tarjeta seleccionada
          }
        );

        if (!response.clientSecret) {
          toast.error('Error al generar el pago.');
          setLoading(false);
          return;
        }

        // Confirmar el pago con Stripe
        const result = await stripe.confirmCardPayment(response.clientSecret, {
          payment_method: paymentMethodId, // Usamos el ID de la tarjeta guardada o nueva
        });

        if (result.error) {
          toast.error(`Error al procesar el pago: ${result.error.message}`);
        } else if (result.paymentIntent?.status === 'succeeded') {
          toast.success(
            player
              ? player.id === user.id
                ? `Has pagado tu parte de la reserva.`
                : `Has pagado la parte de ${player.memberId.name}.`
              : `Has pagado la reserva completa.`
          );
          onClose();
        }
      }
    } catch (error) {
      toast.error('Ocurrió un error al procesar el pago.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {player
          ? player.id === user.id
            ? 'Realizar pago'
            : `Realizar pago`
          : 'Pagar Total'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          {player
            ? player.id === user.id
              ? `Estás a punto de pagar tu parte.`
              : `Estás a punto de pagar la parte de ${player.memberId.name}.`
            : 'Estás a punto de pagar la totalidad de la reserva.'}
        </Typography>
        <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
          Total a Pagar:{' '}
          {player ? player.amount - player.amountPaid : totalAmount} €
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Saldo disponible en monedero: {walletBalance.toFixed(2)} €
        </Typography>
        {walletBalance < totalAmount && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Saldo insuficiente.{' '}
            <Link
              onClick={() => navigate('/profile')}
              sx={{ cursor: 'pointer' }}
            >
              Recarga tu monedero aquí
            </Link>
          </Typography>
        )}
        {/* Selección del método de pago */}
        <RadioGroup
          row
          value={paymentMethod}
          onChange={e => setPaymentMethod(e.target.value)}
        >
          <FormControlLabel
            value="wallet"
            control={<Radio />}
            label="Pagar con saldo del monedero"
            disabled={walletBalance < totalAmount}
          />
          <FormControlLabel
            value="card"
            control={<Radio />}
            label="Pagar con tarjeta"
            disabled={totalAmount < 5}
          />
        </RadioGroup>

        {/* Stripe Card Element solo si se elige tarjeta */}
        {paymentMethod === 'card' && savedCards.length > 0 && (
          <>
            <Typography variant="subtitle1">
              Selecciona una tarjeta guardada o usa una nueva:
            </Typography>
            <RadioGroup
              value={selectedCard}
              onChange={e => setSelectedCard(e.target.value)}
            >
              {savedCards.map(card => (
                <FormControlLabel
                  key={card.id}
                  value={card.id}
                  control={<Radio />}
                  label={`${card.brand.toUpperCase()} **** ${card.last4} (Exp: ${card.expMonth}/${card.expYear})`}
                />
              ))}
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Usar nueva tarjeta"
              />
            </RadioGroup>
          </>
        )}

        {paymentMethod === 'card' && selectedCard === 'new' && (
          <div
            style={{
              marginTop: '20px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          >
            <CardElement options={{ hidePostalCode: true }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveCard}
                  onChange={e => setSaveCard(e.target.checked)}
                />
              }
              label="Guardar tarjeta para futuros pagos"
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handlePayment}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            (paymentMethod === 'wallet' && walletBalance < totalAmount) ||
            (paymentMethod === 'card' &&
              (totalAmount < 5 || !stripe || !elements))
          }
        >
          {loading ? <CircularProgress size={24} /> : 'Pagar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Envolver el diálogo con Stripe Elements
const TotalPaymentDialogWrapper = props => (
  <Elements stripe={stripePromise}>
    <TotalPaymentDialog {...props} />
  </Elements>
);

export default TotalPaymentDialogWrapper;
